/**
 * Editorial content for SEO landing pages. One paragraph per location/type
 * with concrete specifics - names of places, seasons, what to do - so Google
 * sees real content and visitors learn something they can act on.
 */

export interface CityContent {
  slug: string;
  name: string;
  region: string; // for breadcrumbs / heading
  intro: string; // 2-3 sentences shown in the hero
  body: string[]; // paragraphs shown below the listings
  bestTime: string;
  knownFor: string[];
  matches: string[]; // city/area name variants we look for in Firestore
}

export interface TypeContent {
  slug: string;
  name: string;
  plural: string;
  intro: string;
  body: string[];
  matches: string[]; // propertyType values to match
}

export const CITIES: CityContent[] = [
  {
    slug: 'batroun',
    name: 'Batroun',
    region: 'North Lebanon',
    intro:
      'Batroun is the Mediterranean side of a Lebanese summer. Old Phoenician sea wall, beach bars on Pierre & Friends stretch, lemonade everyone fights over.',
    body: [
      'Batroun sits about an hour north of Beirut on the coast. The old town is walkable in 20 minutes; the seafront stretches another 4 km of beach clubs and seafood. From May to September the town is busy. Book at least two weeks ahead for weekends.',
      'The town centre has a phoenician sea wall you can walk on at sunset, a 17th-century church, and a stretch of stone alleys with small bars. North of town the coastline opens into the Pierre & Friends area, where most of the beach clubs and music venues are.',
      'Day trips from here: 30 minutes inland to Tannourine for hiking, 20 minutes south to Byblos for the old harbour, or 90 minutes north to the Cedars and Bcharre.',
    ],
    bestTime: 'May to September',
    knownFor: ['beach clubs', 'Phoenician ruins', 'Batroun lemonade', 'seafood'],
    matches: ['batroun', 'kfaraabida', 'kfar abida', 'chabtine'],
  },
  {
    slug: 'faraya',
    name: 'Faraya',
    region: 'Mount Lebanon',
    intro:
      'Faraya is Lebanon\'s ski town in winter and a cool mountain escape the rest of the year. Mzaar resort is the biggest in the Middle East.',
    body: [
      'Faraya is 50 minutes from Beirut, climbing from sea level to 1,800m. Mzaar Kfardebian, the main ski resort, runs December through March with about 80 km of pistes across Jonction, Wardeh, and Refuge.',
      'Off-season (April to November) the same hills make for cooler nights, hiking trails, and weekend escapes from the coast. A chalet in summer goes for half what it costs in ski season.',
      'Don\'t miss: Faqra Roman ruins (10 minutes from town), Baatara sinkhole at Tannourine (40 minutes), and the Sunday brunch at any of the chalet hotels in Mzaar.',
    ],
    bestTime: 'December to March for skiing, June to September for cool nights',
    knownFor: ['skiing', 'Mzaar resort', 'mountain chalets', 'Roman ruins'],
    matches: ['faraya', 'kfardebian', 'mzaar', 'aalali'],
  },
  {
    slug: 'ehden',
    name: 'Ehden',
    region: 'North Lebanon',
    intro:
      'Ehden is where Beirutis go when the city hits 35°C. A mountain town at 1,500m with cedars, hiking trails, and food that earned its own reputation.',
    body: [
      'Ehden is the summer half of Zgharta. Most families have a house here for July and August and another in Zgharta for winter. The midan (town square) is the centre of everything: cafés, restaurants, the church.',
      'Above town is the Horsh Ehden nature reserve, one of the few remaining cedar forests in Lebanon. Open June through October. Two hours of marked trails, plus longer hikes for the prepared.',
      'Eat at: any of the seven or eight family-run restaurants on the midan. Order kibbeh nayyeh, hummus with meat, and a salad of whatever was picked that morning.',
    ],
    bestTime: 'June to October',
    knownFor: ['cedar forests', 'hiking', 'Lebanese mountain food', 'cool summers'],
    matches: ['ehden', 'zgharta', 'kfarsghab'],
  },
  {
    slug: 'byblos',
    name: 'Byblos',
    region: 'North Lebanon',
    intro:
      'Byblos (Jbeil) is a 7,000-year-old port town with a Crusader castle, a Phoenician harbour, and stone alleys you can get lost in.',
    body: [
      'Byblos is 40 minutes from Beirut on the coastal road. The old town and the harbour form a compact area you can walk in two hours. Don\'t miss the citadel, the wax museum, and the souk at sunset.',
      'For food: Pepe Abed Fishing Club for old-school seafood, Locanda à la Granda for Italian, or any of the small kiosks on the harbour wall for fresh-caught fish grilled on the spot.',
      'Stay anywhere within 5 minutes of the souk. Most listings are renovated old-town stone houses with thick walls and small interior courtyards.',
    ],
    bestTime: 'April to October',
    knownFor: ['old harbour', 'Crusader castle', 'stone souks', 'seafood'],
    matches: ['byblos', 'jbeil', 'kfarhata'],
  },
  {
    slug: 'beirut',
    name: 'Beirut',
    region: 'Capital',
    intro:
      'Beirut for a weekend: bar streets in Mar Mikhael, walking tours in Gemmayze, the Corniche at sunset, food everywhere.',
    body: [
      'Stay in Mar Mikhael, Gemmayze, Achrafieh, or Hamra to be in walking distance of restaurants and bars. Avoid suburbs unless you have a car or don\'t mind 30-minute taxi rides each way.',
      'Walk the Corniche from Ain el Mreisseh to Raouché at sunset, the way locals do every evening. For food, Em Sherif for old Beirut, Tawlet for regional Lebanese cooking, or any of the dozens of small Armenian, Syrian, and Palestinian places in Bourj Hammoud.',
      'Beirut traffic is real. Plan to spend 20-40 minutes in a car for any cross-city trip. A 5-minute walk and a $3 Uber will get you most places within the eastern districts.',
    ],
    bestTime: 'April to June, September to November',
    knownFor: ['nightlife', 'restaurants', 'walking neighbourhoods', 'art galleries'],
    matches: ['beirut', 'achrafieh', 'gemmayze', 'mar mikhael', 'hamra', 'st gerges street'],
  },
  {
    slug: 'tannourine',
    name: 'Tannourine',
    region: 'North Lebanon',
    intro:
      'Tannourine is the Lebanon you see in tourism posters: cedar reserve, the Baatara sinkhole, terraced valleys.',
    body: [
      'Tannourine is 90 minutes from Beirut, an hour from Batroun. The drive itself is the point: the road climbs through Tannourine el Faouqa, past the Baatara Gorge waterfall (open May to October), and ends in the cedar reserve at 2,000m.',
      'Stay nearby for: the cedar reserve hikes, the Baatara sinkhole, mountain biking trails, and quiet roads where you can drive for an hour without seeing another car.',
      'Pack a jacket even in summer. Nights drop to 12°C at altitude. Most chalets have a fireplace and you\'ll want to use it.',
    ],
    bestTime: 'May to October',
    knownFor: ['cedar reserve', 'Baatara sinkhole', 'hiking', 'mountain quiet'],
    matches: ['tannourine', 'tannourine el faouqa', 'tannourine el tahta'],
  },
];

export const TYPES: TypeContent[] = [
  {
    slug: 'chalet',
    name: 'Chalet',
    plural: 'Chalets',
    intro:
      'Chalets in Lebanon are mountain houses, usually with a fireplace, terrace, and a view of either ski slopes or pine forest.',
    body: [
      'Most Lebanese chalets are between 1,000m and 1,800m altitude: Faraya, Kfardebian, Mzaar, Ehden, Bqaatouta. Built for cold months but rented year-round.',
      'What to expect: 2-4 bedrooms, kitchen, fireplace or wood stove, balcony or garden. Heating is usually diesel central or gas in winter; AC isn\'t always included because nights are cool even in summer.',
      'Best chalet bookings come December to March (ski season) and June to September (escape from coastal heat). Off-season prices drop 30-50%.',
    ],
    matches: ['chalet'],
  },
  {
    slug: 'apartment',
    name: 'Apartment',
    plural: 'Apartments',
    intro:
      'Apartments cover the city stays: Beirut weekends, Byblos old town, the residential parts of Batroun and Jounieh.',
    body: [
      'Most listed apartments have 1-3 bedrooms, a kitchen, and a small balcony. In Beirut and Jounieh expect 24/7 generator backup; ask the host if it\'s included in the price.',
      'For Beirut weekends, Mar Mikhael and Gemmayze put you closest to the bar streets. For longer stays, Hamra and Achrafieh have more groceries and quieter nights.',
    ],
    matches: ['apartment'],
  },
  {
    slug: 'house',
    name: 'House',
    plural: 'Houses',
    intro:
      'Standalone houses, mostly in coastal towns and mountain villages. Bigger than apartments, often with gardens.',
    body: [
      'Lebanese houses range from 100-year-old village stone houses (renovated, with thick walls and arched ceilings) to modern 2010s coastal villas. Almost all have outdoor space: a garden, a terrace, sometimes both.',
      'Good for groups of 4-8, family stays, or couples who want privacy and a kitchen they can actually cook in. Most houses have parking; in old towns of Byblos or Batroun you might need to park outside the souk.',
    ],
    matches: ['house'],
  },
  {
    slug: 'cabin',
    name: 'Cabin',
    plural: 'Cabins',
    intro:
      'Wooden cabins in the mountains and forests. Small, simple, and quiet.',
    body: [
      'Most cabins are 30-60 m², 1-2 bedrooms, with basic kitchen and a wood stove. They\'re built into pine and oak forests in Bqaatouta, Tannourine, Ehden, and Akoura.',
      'Best for couples or small families who want to disconnect. Cell signal can be patchy in deeper forest areas, so confirm with the host before booking if you need to work remotely.',
    ],
    matches: ['cabin'],
  },
  {
    slug: 'villa',
    name: 'Villa',
    plural: 'Villas',
    intro:
      'Larger houses, often with a pool, suited to families or groups of 6-12.',
    body: [
      'Villas in Batroun, Faqra, and Jounieh come with private pools (open May to October), 3-6 bedrooms, and full kitchens. Cleaning fees are higher but per-person costs come out lower than equivalent hotel rooms.',
      'Pool maintenance is on the host. If a pool issue comes up during your stay, message the host first; if it\'s not resolved within a few hours, contact us for a partial refund.',
    ],
    matches: ['villa'],
  },
  {
    slug: 'caravan',
    name: 'Caravan',
    plural: 'Caravans',
    intro:
      'Static caravans and mobile homes parked in scenic spots. The cheapest mountain or coast option.',
    body: [
      'Most caravans on DARAK are static, parked permanently on a piece of private land, hooked up to electricity and water. Inside: bed, basic kitchenette, small bathroom.',
      'Good for budget weekend escapes or solo travellers. Bring extra blankets in winter. Caravans don\'t hold heat the way stone houses do.',
    ],
    matches: ['caravan'],
  },
  {
    slug: 'studio',
    name: 'Studio',
    plural: 'Studios',
    intro:
      'Single-room places with a bed, kitchenette, and bathroom, for solo trips or short city stays.',
    body: [
      'Studios are mostly in Beirut and Jounieh. They suit short business trips or solo weekends; couples should size up to a 1-bed apartment.',
    ],
    matches: ['studio'],
  },
];

export function findCity(slug: string): CityContent | undefined {
  return CITIES.find((c) => c.slug === slug);
}

export function findType(slug: string): TypeContent | undefined {
  return TYPES.find((t) => t.slug === slug);
}

export function listingMatchesCity(listing: { location?: { city?: string; area?: string } }, city: CityContent): boolean {
  const c = (listing.location?.city || '').toLowerCase().trim();
  const a = (listing.location?.area || '').toLowerCase().trim();
  return city.matches.some((m) => c.includes(m) || a.includes(m));
}

export function listingMatchesType(listing: { propertyType?: string }, type: TypeContent): boolean {
  const t = (listing.propertyType || '').toLowerCase().trim();
  return type.matches.some((m) => t === m || t.includes(m));
}
